import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import AddTaskDialog from "@/components/AddTaskDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Compass, ListChecks, CalendarRange, Target, Feather,
  FileText, Facebook, Instagram, Image as ImageIcon, Mail, Youtube, CheckCircle2,
  Linkedin, AtSign, Twitter, Music2, Newspaper, Hash, ChevronLeft, ChevronRight, Plus,
} from "lucide-react";

const FORMAT_CATALOG = {
  blog: { label: "Blog", icon: FileText },
  facebook: { label: "Facebook", icon: Facebook },
  instagram: { label: "Instagram", icon: Instagram },
  linkedin: { label: "LinkedIn", icon: Linkedin },
  pinterest: { label: "Pinterest", icon: ImageIcon },
  email: { label: "Email", icon: Mail },
  youtube: { label: "YouTube", icon: Youtube },
  threads: { label: "Threads", icon: AtSign },
  x: { label: "X (Twitter)", icon: Twitter },
  tiktok: { label: "TikTok", icon: Music2 },
  newsletter: { label: "Newsletter", icon: Newspaper },
};

const STAGE_TONE = {
  Idea: "bg-muted text-muted-foreground",
  Draft: "bg-accent/20 text-secondary",
  Scheduled: "bg-secondary/15 text-secondary",
  Completed: "bg-primary text-primary-foreground",
};

const CATEGORY_TONE = {
  Blog: "bg-primary/10 text-primary", Pinterest: "bg-accent/20 text-secondary",
  Facebook: "bg-primary/10 text-primary", Instagram: "bg-accent/20 text-secondary",
  Email: "bg-secondary/10 text-secondary", App: "bg-primary/10 text-primary",
  Website: "bg-accent/20 text-secondary", Business: "bg-secondary/10 text-secondary",
  School: "bg-muted text-muted-foreground", Personal: "bg-muted text-muted-foreground",
};

function mondayOf(dateLike) {
  // Timezone-safe: compute the Monday of the UTC week so it always matches the backend (UTC Monday).
  let d;
  if (typeof dateLike === "string") {
    d = new Date(dateLike + "T00:00:00Z");
  } else {
    const n = new Date();
    d = new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
  }
  const day = (d.getUTCDay() + 6) % 7; // 0 = Monday
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtRange(monday) {
  const end = addDays(monday, 6);
  const a = new Date(monday + "T00:00:00Z");
  const b = new Date(end + "T00:00:00Z");
  const opts = { month: "short", day: "numeric", timeZone: "UTC" };
  return `${a.toLocaleDateString(undefined, opts)} – ${b.toLocaleDateString(undefined, { ...opts, year: "numeric" })}`;
}

const CURRENT_MONDAY = mondayOf(new Date());
const EMPTY_WEEK = { primary_focus: "", priorities: ["", "", ""], must_complete: "", reflect_worked: "", reflect_heavy: "", reflect_simplified: "", reflect_carry_forward: "" };

const SectionCard = ({ icon: Icon, title, hint, children, testid, action }) => (
  <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid={testid}>
    <div className="flex items-start gap-3 mb-5">
      <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
      </div>
      <div className="flex-1">
        <h2 className="font-heading text-xl">{title}</h2>
        {hint && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </Card>
);

export default function Planning() {
  const [monthly, setMonthly] = useState(null);        // global planning doc (monthly fields)
  const [weekPlan, setWeekPlan] = useState(EMPTY_WEEK); // per-week This Week + Reflection
  const [weeks, setWeeks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(CURRENT_MONDAY);
  const [loading, setLoading] = useState(true);
  const [weekLoading, setWeekLoading] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  // Initial load: monthly plan + content weeks + tasks (once).
  const load = useCallback(async () => {
    try {
      const [p, w, t] = await Promise.all([
        api.get("/planning"),
        api.get("/weeks").catch(() => ({ data: [] })),
        api.get("/tasks").catch(() => ({ data: [] })),
      ]);
      setMonthly(p.data || {});
      setWeeks(w.data || []);
      setTasks(t.data || []);
    } catch (e) {
      toast.error("Could not load your planning space.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Load the selected week's This Week + Reflection whenever the week changes.
  useEffect(() => {
    let active = true;
    setWeekLoading(true);
    api.get(`/planning/week/${selectedWeek}`)
      .then((r) => {
        if (!active) return;
        const doc = r.data || {};
        if (!Array.isArray(doc.priorities)) doc.priorities = ["", "", ""];
        while (doc.priorities.length < 3) doc.priorities.push("");
        setWeekPlan({ ...EMPTY_WEEK, ...doc, priorities: doc.priorities.slice(0, 3) });
      })
      .catch(() => { if (active) setWeekPlan({ ...EMPTY_WEEK, priorities: ["", "", ""] }); })
      .finally(() => { if (active) setWeekLoading(false); });
    return () => { active = false; };
  }, [selectedWeek]);

  const refreshTasks = useCallback(async () => {
    try { const t = await api.get("/tasks"); setTasks(t.data || []); } catch (e) { /* no-op */ }
  }, []);

  // ---- Persistence
  const persistWeek = async (next) => {
    try { await api.put(`/planning/week/${selectedWeek}`, next); }
    catch (e) { toast.error("Could not save this week. Please try again."); }
  };
  const persistMonthly = async (doc) => {
    try { await api.put("/planning", { monthly_goal: doc.monthly_goal || "", monthly_progress: doc.monthly_progress || "" }); }
    catch (e) { toast.error("Could not save. Please try again."); }
  };

  const setWeekField = (key, value) => setWeekPlan((p) => ({ ...p, [key]: value }));
  const saveWeek = () => persistWeek(weekPlan);
  const setPriority = (idx, value) =>
    setWeekPlan((p) => {
      const priorities = [...(p.priorities || ["", "", ""])];
      priorities[idx] = value;
      return { ...p, priorities };
    });

  const setMonthlyField = (key, value) => setMonthly((m) => ({ ...m, [key]: value }));
  const saveMonthly = () => { if (monthly) persistMonthly(monthly); };

  // ---- Content Rhythm: the content week that matches the selected calendar week (reads /weeks, unchanged sync)
  const contentWeek = useMemo(
    () => weeks.find((w) => w.week_start && mondayOf(w.week_start) === selectedWeek) || null,
    [weeks, selectedWeek]
  );

  // ---- Open Tasks: genuine unfinished work for the SELECTED week (by due date). Undated tasks show
  // only in the current week so they aren't lost and don't appear duplicated across weeks.
  const weekEnd = useMemo(() => addDays(selectedWeek, 6), [selectedWeek]);
  const openTasks = useMemo(() => tasks.filter((t) => {
    if (t.completed) return false;
    if (t.due_date) return t.due_date >= selectedWeek && t.due_date <= weekEnd;
    return selectedWeek === CURRENT_MONDAY;
  }), [tasks, selectedWeek, weekEnd]);

  const completeTask = async (task) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: true } : t)));
    try { await api.put(`/tasks/${task.id}`, { completed: true }); toast.success("Done. One less thing to hold."); }
    catch (e) { toast.error("Could not update the task."); refreshTasks(); }
  };

  const rel = selectedWeek === CURRENT_MONDAY ? "This week" : selectedWeek < CURRENT_MONDAY ? "Past week" : "Upcoming week";

  if (loading || !monthly) {
    return (
      <div>
        <PageHeader testid="planning-header" title="Planning" subtitle="When you open Planning, you should immediately know what matters this week, and what can wait." />
        <Card className="rounded-3xl border-border shadow-soft p-12 text-center text-muted-foreground" data-testid="planning-loading">Gathering your week…</Card>
      </div>
    );
  }

  return (
    <div data-testid="planning-page">
      <PageHeader
        testid="planning-header"
        title="Planning"
        subtitle="When you open Planning, you should immediately know what matters this week, and what can wait."
      />

      {/* Week navigation */}
      <Card className="rounded-3xl border-border shadow-soft px-4 py-3 mb-6 flex items-center justify-between gap-3" data-testid="week-nav">
        <Button variant="ghost" size="sm" onClick={() => setSelectedWeek((w) => addDays(w, -7))}
          data-testid="week-nav-prev" className="rounded-full gap-1">
          <ChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Previous</span>
        </Button>
        <div className="text-center min-w-0">
          <p className="font-heading text-base sm:text-lg leading-tight truncate" data-testid="week-label">{fmtRange(selectedWeek)}</p>
          <p className="text-xs text-muted-foreground" data-testid="week-relation">{rel}</p>
        </div>
        <div className="flex items-center gap-1">
          {selectedWeek !== CURRENT_MONDAY && (
            <Button variant="outline" size="sm" onClick={() => setSelectedWeek(CURRENT_MONDAY)}
              data-testid="week-nav-today" className="rounded-full">This week</Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSelectedWeek((w) => addDays(w, 7))}
            data-testid="week-nav-next" className="rounded-full gap-1">
            <span className="hidden sm:inline">Next</span> <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* This Week (per selected week) */}
      <SectionCard icon={Compass} title="This Week" hint="Your one focus, up to three priorities, and anything that must be finished, for the week shown above." testid="section-this-week">
        <fieldset disabled={weekLoading} className="space-y-5">
          <div className="space-y-2">
            <Label>Primary focus</Label>
            <Input
              data-testid="primary-focus-input"
              value={weekPlan.primary_focus || ""}
              onChange={(e) => setWeekField("primary_focus", e.target.value)}
              onBlur={saveWeek}
              placeholder="The one thing that matters most this week…"
              className="rounded-xl text-base"
            />
          </div>
          <div className="space-y-2">
            <Label>Top priorities <span className="text-muted-foreground font-normal">(up to three)</span></Label>
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Input
                  key={i}
                  data-testid={`priority-input-${i}`}
                  value={weekPlan.priorities?.[i] || ""}
                  onChange={(e) => setPriority(i, e.target.value)}
                  onBlur={saveWeek}
                  placeholder={`Priority ${i + 1}`}
                  className="rounded-xl"
                />
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Must be completed this week</Label>
            <Textarea
              data-testid="must-complete-input"
              value={weekPlan.must_complete || ""}
              onChange={(e) => setWeekField("must_complete", e.target.value)}
              onBlur={saveWeek}
              placeholder="Anything that truly cannot wait…"
              className="rounded-xl min-h-[90px]"
            />
          </div>
        </fieldset>
      </SectionCard>

      {/* Content Rhythm */}
      <SectionCard icon={CalendarRange} title="Content Rhythm" hint="This week's theme and where each format stands, pulled from your Content Command Center." testid="section-content-rhythm">
        {contentWeek ? (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="text-sm text-muted-foreground">{fmtRange(selectedWeek)}</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-sm">Theme: <span className="font-medium" data-testid="rhythm-theme">{contentWeek.core_theme || contentWeek.theme || "—"}</span></span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="rhythm-formats">
              {Object.keys(contentWeek.formats || {}).map((key) => {
                const meta = FORMAT_CATALOG[key] || { label: key, icon: Hash };
                const Icon = meta.icon;
                const fmt = contentWeek.formats?.[key] || {};
                const stage = fmt.stage || "Idea";
                const hasContent = Boolean((fmt.content || "").trim());
                return (
                  <div key={key} className="flex items-center gap-3 rounded-2xl bg-muted/30 border border-border px-4 py-3" data-testid={`rhythm-${key}`}>
                    <Icon className="h-4 w-4 text-primary shrink-0" strokeWidth={1.75} />
                    <span className="text-sm flex-1">{meta.label}</span>
                    {hasContent && <CheckCircle2 className="h-4 w-4 text-primary/60" />}
                    <Badge className={`rounded-full font-normal text-xs border-0 ${STAGE_TONE[stage]}`}>{stage}</Badge>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="rhythm-empty">
            No content week for {fmtRange(selectedWeek)}. Start one in the Content Command Center and it will appear here.
          </p>
        )}
      </SectionCard>

      {/* Open Tasks (scoped to selected week) */}
      <SectionCard
        icon={ListChecks}
        title="Open Tasks"
        hint="Unfinished work for the week shown above. Check one off when it's done."
        testid="section-open-tasks"
        action={
          <Button variant="outline" size="sm" onClick={() => setTaskDialogOpen(true)} data-testid="add-task-button" className="rounded-full gap-1 shrink-0">
            <Plus className="h-4 w-4" /> Add task
          </Button>
        }
      >
        {openTasks.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="open-tasks-empty">Nothing open for this week. A quiet, clear list.</p>
        ) : (
          <div className="space-y-1" data-testid="open-tasks-list">
            {openTasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-2.5 px-3 rounded-2xl hover:bg-muted/40 transition-colors" data-testid={`open-task-${t.id}`}>
                <Checkbox checked={false} onCheckedChange={() => completeTask(t)} data-testid={`open-task-check-${t.id}`}
                  className="rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                <span className="text-sm flex-1 min-w-0">{t.title}</span>
                {t.category && <Badge className={`rounded-full font-normal text-xs border-0 ${CATEGORY_TONE[t.category] || "bg-muted text-muted-foreground"}`}>{t.category}</Badge>}
                {t.due_date && <span className="text-xs text-muted-foreground">{t.due_date}</span>}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Monthly Focus (persists across weeks, by design) */}
      <SectionCard icon={Target} title="Monthly Focus" hint="One main business goal for the month, and a place to note progress." testid="section-monthly-focus">
        <div className="space-y-2 mb-5">
          <Label>Main goal this month</Label>
          <Input
            data-testid="monthly-goal-input"
            value={monthly.monthly_goal || ""}
            onChange={(e) => setMonthlyField("monthly_goal", e.target.value)}
            onBlur={saveMonthly}
            placeholder="The one business goal you're moving toward…"
            className="rounded-xl text-base"
          />
        </div>
        <div className="space-y-2">
          <Label>Progress &amp; reflection</Label>
          <Textarea
            data-testid="monthly-progress-input"
            value={monthly.monthly_progress || ""}
            onChange={(e) => setMonthlyField("monthly_progress", e.target.value)}
            onBlur={saveMonthly}
            placeholder="How is it going? What have you noticed?"
            className="rounded-xl min-h-[100px]"
          />
        </div>
      </SectionCard>

      {/* Reflection (per selected week) */}
      <SectionCard icon={Feather} title="Reflection" hint="A gentle pause for this week. Answer as honestly or briefly as you like." testid="section-reflection">
        <fieldset disabled={weekLoading} className="grid sm:grid-cols-2 gap-5">
          {[
            { key: "reflect_worked", label: "What worked?" },
            { key: "reflect_heavy", label: "What felt heavy?" },
            { key: "reflect_simplified", label: "What can be simplified?" },
            { key: "reflect_carry_forward", label: "What should carry forward?" },
          ].map((r) => (
            <div key={r.key} className="space-y-2">
              <Label>{r.label}</Label>
              <Textarea
                data-testid={`reflection-${r.key}`}
                value={weekPlan[r.key] || ""}
                onChange={(e) => setWeekField(r.key, e.target.value)}
                onBlur={saveWeek}
                placeholder="…"
                className="rounded-xl min-h-[90px]"
              />
            </div>
          ))}
        </fieldset>
      </SectionCard>

      <AddTaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        task={{ due_date: selectedWeek }}
        onSaved={refreshTasks}
      />
    </div>
  );
}
